import React, { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { API_URL } from '../../config/api';
import { formatCurrency } from '../../utils/dateUtils';
import './SystemAdmin.css';

// Valid locations for user assignment
const LOCATIONS = ['All', 'Pomona', 'Lakewood', 'Israel', 'China'];
const USER_LOCATIONS = ['Pomona', 'Lakewood', 'Israel'];
const COUNTRY_CODES = [
  { value: 'US', label: 'USA' },
  { value: 'IL', label: 'Israel' }
];
const ORDER_TYPES = ['All', 'New Wig', 'Aidel New Wig', 'Wig Repair', 'Aidel Wig Repair', 'Accessory', 'Wash & Set', 'New Sale Wig'];
const PAYMENT_TYPES = ['All', 'New Wig Deposit', 'Payment', 'Tax Payment','New Sale Wig Payment', 'Adjustment'];

// Repair types for dropdown
const REPAIR_TYPES = ['Delivery', 'HandSew', 'Styling', 'Color', 'Wefts', 'Wig Repair', 'Machine', 'Topper'];

// Accessory categories for dropdown
const ACCESSORY_CATEGORIES = ['Accessories', 'Wig Heads', 'Wig Boxes', 'Teeny Dini', 'Bands'];

// Date range presets (matching legacy)
const DATE_PRESETS = [
  { label: 'Today', days: 0 },
  { label: 'Yesterday', days: 1 },
  { label: 'Last Week', days: 7 },
  { label: 'Last 2 Weeks', days: 14 },
  { label: 'Last 3 Weeks', days: 21 },
  { label: 'Last 4 Weeks', days: 28 },
  { label: 'Last 3 Months', days: 90 },
  { label: 'Last 6 Months', days: 180 },
  { label: 'Last Year', days: 365 },
];

const SystemAdmin = ({ user }) => {
  const [activeTab, setActiveTab] = useState('deleteOrder');

  // Delete Order state
  const [orderNo, setOrderNo] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);

  // Payment Report state
  const [datePreset, setDatePreset] = useState('Today');
  const [startDate, setStartDate] = useState(formatDate(new Date()));
  const [endDate, setEndDate] = useState(formatDate(new Date()));
  const [location, setLocation] = useState('All');
  const [payments, setPayments] = useState([]);
  const [paymentType, setPaymentType] = useState('All');
  const [orderType, setOrderType] = useState('All');
  const [totals, setTotals] = useState({ total: 0, byMethod: {}, countByMethod: {}, adjustmentsTotal: 0, adjustmentsCount: 0 });
  const [weeklyData, setWeeklyData] = useState({ currentWeek: [], previousWeek: [] });
  const [reportLoading, setReportLoading] = useState(false);
  const [reportError, setReportError] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [chartsReady, setChartsReady] = useState(false);

  // Price List state
  const [priceSubTab, setPriceSubTab] = useState('repairs');
  const [repairs, setRepairs] = useState([]);
  const [accessories, setAccessories] = useState([]);
  const [wigPricing, setWigPricing] = useState([]);
  const [priceLoading, setPriceLoading] = useState(false);
  const [priceError, setPriceError] = useState(null);
  const [priceSuccess, setPriceSuccess] = useState(null);
  const [editingItem, setEditingItem] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('All');

  // User Management state
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [usersError, setUsersError] = useState(null);
  const [usersSuccess, setUsersSuccess] = useState(null);
  const [editingUser, setEditingUser] = useState(null);
  const [showAddUserForm, setShowAddUserForm] = useState(false);
  const [userSearchTerm, setUserSearchTerm] = useState('');
  const [userLocationFilter, setUserLocationFilter] = useState('All');
  const [showPassword, setShowPassword] = useState(false);

  // Manage Workers (Room Assignments) state
  const [rooms, setRooms] = useState([]);
  const [allWorkers, setAllWorkers] = useState({}); // Worker name -> array of room assignments
  const [workersLoading, setWorkersLoading] = useState(false);
  const [workersError, setWorkersError] = useState(null);
  const [workersSuccess, setWorkersSuccess] = useState(null);
  const [showAddWorkerForm, setShowAddWorkerForm] = useState(false);
  const [showAddNewWorker, setShowAddNewWorker] = useState(false);
  const [newWorkerName, setNewWorkerName] = useState('');
  const [selectedWorkerLocation, setSelectedWorkerLocation] = useState(null);
  const [selectedWorkerRoom, setSelectedWorkerRoom] = useState(null);
  const [roomWorkers, setRoomWorkers] = useState([]);
  const [showAddRoomForm, setShowAddRoomForm] = useState(false);
  const [newRoom, setNewRoom] = useState({ RoomNo: '', RoomDescription: '', ParentRoomNo: '' });
  const [editingRoomNo, setEditingRoomNo] = useState(null);
  const [editingRoomName, setEditingRoomName] = useState('');

  // Format date for input fields — uses local time components to avoid UTC date shift
  function formatDate(date) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  // Check if user is management (admin)
  const isAdmin = user?.IsManagement === true || user?.IsManagement === 1;

  // Load data when price tab is selected (must be before conditional returns)
  useEffect(() => {
    if (!user || !isAdmin) return;
    if (activeTab !== 'priceList') return;

    const fetchData = async () => {
      setPriceLoading(true);
      setPriceError(null);
      try {
        let endpoint = '';
        if (priceSubTab === 'repairs') endpoint = '/api/admin/repairs';
        else if (priceSubTab === 'accessories') endpoint = '/api/admin/accessories';
        else if (priceSubTab === 'wigPricing') endpoint = '/api/admin/wig-pricing';

        const response = await fetch(`${API_URL}${endpoint}`, {
          headers: { 'x-username': user.UserName }
        });
        const data = await response.json();

        if (data.success) {
          if (priceSubTab === 'repairs') setRepairs(data.repairs);
          else if (priceSubTab === 'accessories') setAccessories(data.accessories);
          else if (priceSubTab === 'wigPricing') setWigPricing(data.pricing);
        } else {
          setPriceError('Failed to fetch data');
        }
      } catch (error) {
        setPriceError('Network error: ' + error.message);
      } finally {
        setPriceLoading(false);
      }
    };

    fetchData();
  }, [activeTab, priceSubTab, user, isAdmin]);

  // Load users when users tab is selected
  useEffect(() => {
    if (!user || !isAdmin) return;
    if (activeTab !== 'users') return;
    fetchUsers();
  }, [activeTab, user, isAdmin]);

  // Load rooms and all workers when workers tab is selected
  useEffect(() => {
    if (!user || !isAdmin) return;
    if (activeTab !== 'workers') return;
    fetchRooms();
    fetchAllWorkers();
  }, [activeTab, user, isAdmin]);

  // Load workers when a room is selected
  useEffect(() => {
    if (!selectedWorkerRoom) {
      setRoomWorkers([]);
      return;
    }
    fetchRoomWorkers(selectedWorkerRoom);
  }, [selectedWorkerRoom]);

  // Fetch all users
  const fetchUsers = async () => {
    setUsersLoading(true);
    setUsersError(null);
    try {
      const response = await fetch(`${API_URL}/api/admin/users`, {
        headers: { 'x-username': user.UserName }
      });
      const data = await response.json();
      if (data.success) {
        setUsers(data.users);
      } else {
        setUsersError(data.error || 'Failed to fetch users');
      }
    } catch (error) {
      setUsersError('Network error: ' + error.message);
    } finally {
      setUsersLoading(false);
    }
  };

  // Save user (create or update)
  const handleSaveUser = async (userData, isNew = false) => {
    setUsersLoading(true);
    setUsersError(null);
    try {
      const url = isNew
        ? `${API_URL}/api/admin/users`
        : `${API_URL}/api/admin/users/${userData.UserID}`;

      const response = await fetch(url, {
        method: isNew ? 'POST' : 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'x-username': user.UserName
        },
        body: JSON.stringify(userData)
      });

      const data = await response.json();
      if (data.success) {
        setUsersSuccess(isNew ? 'User created successfully' : 'User updated successfully');
        setEditingUser(null);
        setShowAddUserForm(false);
        fetchUsers();
        setTimeout(() => setUsersSuccess(null), 3000);
      } else {
        setUsersError(data.error || 'Failed to save user');
        setTimeout(() => setUsersError(null), 5000);
      }
    } catch (error) {
      setUsersError('Network error: ' + error.message);
      setTimeout(() => setUsersError(null), 5000);
    } finally {
      setUsersLoading(false);
    }
  };

  // Delete user
  const handleDeleteUser = async (userId, username) => {

    setUsersLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/users/${userId}`, {
        method: 'DELETE',
        headers: { 'x-username': user.UserName }
      });

      const data = await response.json();
      if (data.success) {
        setUsersSuccess('User deleted successfully');
        fetchUsers();
        setTimeout(() => setUsersSuccess(null), 3000);
      } else {
        setUsersError(data.error || 'Failed to delete user');
        setTimeout(() => setUsersError(null), 5000);
      }
    } catch (error) {
      setUsersError('Network error: ' + error.message);
      setTimeout(() => setUsersError(null), 5000);
    } finally {
      setUsersLoading(false);
    }
  };

  // Fetch all rooms for dropdown
  const fetchRooms = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/rooms`, {
        headers: { 'x-username': user.UserName }
      });
      const data = await response.json();
      if (data.success) {
        setRooms(data.rooms);
      }
    } catch (error) {
      console.error('Failed to fetch rooms:', error);
    }
  };

  // Fetch all workers with their room assignments
  const fetchAllWorkers = async () => {
    setWorkersLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/room-workers`, {
        headers: { 'x-username': user.UserName }
      });
      const data = await response.json();
      if (data.success) {
        // Group assignments by worker name
        const workerMap = {};
        data.assignments.forEach(a => {
          if (!workerMap[a.UserName]) {
            workerMap[a.UserName] = [];
          }
          workerMap[a.UserName].push({
            roomNo: a.RoomNo,
            roomDescription: a.RoomDescription,
            roomLocation: a.RoomLocation
          });
        });
        setAllWorkers(workerMap);
      }
    } catch (error) {
      console.error('Failed to fetch workers:', error);
    } finally {
      setWorkersLoading(false);
    }
  };

  // Fetch workers for a specific room
  const fetchRoomWorkers = async (roomNo) => {
    setWorkersLoading(true);
    setWorkersError(null);
    try {
      const response = await fetch(`${API_URL}/api/admin/room-workers`, {
        headers: { 'x-username': user.UserName }
      });
      const data = await response.json();
      if (data.success) {
        // Filter to only workers for this room
        const workersForRoom = data.assignments.filter(a => a.RoomNo === roomNo);
        setRoomWorkers(workersForRoom);
      } else {
        setWorkersError(data.error || 'Failed to fetch room workers');
      }
    } catch (error) {
      setWorkersError('Network error: ' + error.message);
    } finally {
      setWorkersLoading(false);
    }
  };

  // Add worker to room
  const handleAddWorkerToRoom = async (userName) => {
    if (!selectedWorkerRoom) return;
    setWorkersLoading(true);
    setWorkersError(null);
    try {
      const response = await fetch(`${API_URL}/api/admin/room-workers`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-username': user.UserName
        },
        body: JSON.stringify({ RoomNo: selectedWorkerRoom, UserName: userName })
      });
      const data = await response.json();
      if (data.success) {
        setWorkersSuccess('Worker assigned to room successfully');
        setShowAddWorkerForm(false);
        fetchRoomWorkers(selectedWorkerRoom);
        fetchAllWorkers(); // Refresh all workers list in case this is a new worker
        setTimeout(() => setWorkersSuccess(null), 3000);
      } else {
        setWorkersError(data.error || 'Failed to assign worker');
        setTimeout(() => setWorkersError(null), 5000);
      }
    } catch (error) {
      setWorkersError('Network error: ' + error.message);
      setTimeout(() => setWorkersError(null), 5000);
    } finally {
      setWorkersLoading(false);
    }
  };

  // Remove worker from room
  const handleRemoveWorkerFromRoom = async (userName) => {
    if (!selectedWorkerRoom) return;
    if (!window.confirm(`Remove "${userName}" from Room ${selectedWorkerRoom}?`)) return;

    setWorkersLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/room-workers/${selectedWorkerRoom}/${userName}`, {
        method: 'DELETE',
        headers: { 'x-username': user.UserName }
      });
      const data = await response.json();
      if (data.success) {
        setWorkersSuccess('Worker removed from room successfully');
        fetchRoomWorkers(selectedWorkerRoom);
        setTimeout(() => setWorkersSuccess(null), 3000);
      } else {
        setWorkersError(data.error || 'Failed to remove worker');
        setTimeout(() => setWorkersError(null), 5000);
      }
    } catch (error) {
      setWorkersError('Network error: ' + error.message);
      setTimeout(() => setWorkersError(null), 5000);
    } finally {
      setWorkersLoading(false);
    }
  };

  // Remove worker from ALL rooms
  const handleRemoveWorkerFromAllRooms = async (userName) => {
    if (!window.confirm(`Remove "${userName}" from ALL rooms?\n\nThis will delete all room assignments for this worker.`)) return;

    setWorkersLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/workers/${userName}`, {
        method: 'DELETE',
        headers: { 'x-username': user.UserName }
      });
      const data = await response.json();
      if (data.success) {
        setWorkersSuccess(`Worker "${userName}" removed from all rooms`);
        fetchAllWorkers();
        setTimeout(() => setWorkersSuccess(null), 3000);
      } else {
        setWorkersError(data.error || 'Failed to remove worker');
        setTimeout(() => setWorkersError(null), 5000);
      }
    } catch (error) {
      setWorkersError('Network error: ' + error.message);
      setTimeout(() => setWorkersError(null), 5000);
    } finally {
      setWorkersLoading(false);
    }
  };

  // Shared request helper for room add/rename/delete
  const submitRoomChange = async (url, method, body, successText) => {
    setWorkersLoading(true);
    setWorkersError(null);
    try {
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'x-username': user.UserName
        },
        body: body ? JSON.stringify(body) : undefined
      });
      const data = await response.json();
      if (data.success) {
        setWorkersSuccess(successText);
        await fetchRooms();
        setTimeout(() => setWorkersSuccess(null), 3000);
        return true;
      }
      setWorkersError(data.error || 'Room update failed');
      setTimeout(() => setWorkersError(null), 5000);
    } catch (error) {
      setWorkersError('Network error: ' + error.message);
      setTimeout(() => setWorkersError(null), 5000);
    } finally {
      setWorkersLoading(false);
    }
    return false;
  };

  // Add room to the selected location
  const handleAddRoom = async () => {
    if (!newRoom.RoomNo.trim() || !newRoom.RoomDescription.trim()) return;
    const ok = await submitRoomChange(
      `${API_URL}/api/admin/rooms`,
      'POST',
      { ...newRoom, RoomLocation: selectedWorkerLocation },
      `Room ${newRoom.RoomNo.trim()} added`
    );
    if (ok) {
      setNewRoom({ RoomNo: '', RoomDescription: '', ParentRoomNo: '' });
      setShowAddRoomForm(false);
    }
  };

  // Rename room
  const handleRenameRoom = async (roomNo) => {
    if (!editingRoomName.trim()) return;
    const ok = await submitRoomChange(
      `${API_URL}/api/admin/rooms/${roomNo}`,
      'PUT',
      { RoomDescription: editingRoomName.trim() },
      `Room ${roomNo} renamed`
    );
    if (ok) {
      setEditingRoomNo(null);
      setEditingRoomName('');
    }
  };

  // Delete room
  const handleDeleteRoom = async (room) => {
    if (!window.confirm(`Delete Room ${room.RoomNo} - ${room.RoomDescription}?\n\nWorker assignments for this room will also be removed.`)) return;
    const ok = await submitRoomChange(
      `${API_URL}/api/admin/rooms/${room.RoomNo}`,
      'DELETE',
      null,
      `Room ${room.RoomNo} deleted`
    );
    if (ok) fetchAllWorkers();
  };

  // Get rooms filtered by selected location
  const filteredRooms = selectedWorkerLocation
    ? rooms.filter(r => r.RoomLocation === selectedWorkerLocation)
    : [];

  // Get unique locations from rooms
  const roomLocations = [...new Set(rooms.map(r => r.RoomLocation))].filter(Boolean);

  // Get selected room details
  const selectedRoomDetails = rooms.find(r => r.RoomNo === selectedWorkerRoom);

  // Filter users by search and location
  const currentUsername = (user?.UserName || '').toLowerCase();
  const filteredUsers = users.filter(u => {
    if ((u.UserName || '').toLowerCase() === 'noga' && currentUsername !== 'noga') return false;
    const matchesSearch =
      (u.UserName?.toLowerCase() || '').includes(userSearchTerm.toLowerCase()) ||
      (u.UserFullName?.toLowerCase() || '').includes(userSearchTerm.toLowerCase()) ||
      (u.UserEmail?.toLowerCase() || '').includes(userSearchTerm.toLowerCase());
    const matchesLocation = userLocationFilter === 'All' || u.UserLocation === userLocationFilter;
    return matchesSearch && matchesLocation;
  });

  // Check authorization - redirect if not admin
  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (!isAdmin) {
    return <Navigate to="/" replace />;
  }

  // Format date for display
  function formatDisplayDate(dateStr) {
    if (!dateStr) return '';
    // Split on 'T' first to strip time, then parse parts directly.
    // Avoids JS treating date-only strings as UTC midnight, which shifts
    // the displayed date back one day in US (negative-offset) timezones.
    const [year, month, day] = dateStr.split('T')[0].split('-');
    return `${month}/${day}/${year}`;
  }


  // Preview order before deletion
  const handlePreviewOrder = async () => {
    if (!orderNo.trim()) {
      setMessage({ type: 'error', text: 'Please enter an order number' });
      return;
    }

    setLoading(true);
    setMessage(null);
    setPreviewData(null);

    try {
      const response = await fetch(`${API_URL}/api/admin/order/${orderNo}/preview`, {
        headers: { 'x-username': user.UserName }
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setPreviewData(data);
        setMessage({
          type: 'info',
          text: `Order #${orderNo} found. Review details below before confirming deletion.`
        });
      } else {
        setMessage({
          type: 'error',
          text: data.error || 'Order not found'
        });
      }
    } catch (error) {
      setMessage({
        type: 'error',
        text: 'Network error: ' + error.message
      });
    } finally {
      setLoading(false);
    }
  };


  // Handle date preset change
  const handlePresetChange = (preset) => {
    setDatePreset(preset);
    const today = new Date();
    const presetConfig = DATE_PRESETS.find(p => p.label === preset);

    if (preset === 'Today') {
      setStartDate(formatDate(today));
      setEndDate(formatDate(today));
    } else if (preset === 'Yesterday') {
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);
      setStartDate(formatDate(yesterday));
      setEndDate(formatDate(yesterday));
    } else if (presetConfig) {
      const pastDate = new Date(today);
      pastDate.setDate(pastDate.getDate() - presetConfig.days);
      setStartDate(formatDate(pastDate));
      setEndDate(formatDate(today));
    }
  };


  // Calculate weekly totals for chart (M-F)
  const calculateWeeklyData = (paymentsData) => {
    const today = new Date();
    const currentWeekStart = new Date(today);
    currentWeekStart.setDate(today.getDate() - today.getDay() + 1); // Monday of current week
    currentWeekStart.setHours(0, 0, 0, 0);

    const previousWeekStart = new Date(currentWeekStart);
    previousWeekStart.setDate(previousWeekStart.getDate() - 7);

    // Initialize daily totals for M-F (indices 0-4)
    const currentWeek = [0, 0, 0, 0, 0]; // Mon-Fri
    const previousWeek = [0, 0, 0, 0, 0]; // Mon-Fri

    paymentsData.forEach(payment => {
      if (payment.isAdjustment) return; // balance write-offs aren't revenue
      const paymentDate = new Date(payment.PaymentDate);
      const dayOfWeek = paymentDate.getDay(); // 0=Sun, 1=Mon, ..., 5=Fri, 6=Sat

      // Only count Monday-Friday (1-5)
      if (dayOfWeek >= 1 && dayOfWeek <= 5) {
        const dayIndex = dayOfWeek - 1; // Convert to 0-4 index
        const amount = parseFloat(payment.PaymentAmount) || 0;

        if (paymentDate >= currentWeekStart) {
          currentWeek[dayIndex] += amount;
        } else if (paymentDate >= previousWeekStart && paymentDate < currentWeekStart) {
          previousWeek[dayIndex] += amount;
        }
      }
    });

    return { currentWeek, previousWeek };
  };

  // Generate payment report
  const handleGenerateReport = async () => {
    setReportLoading(true);
    setReportError(null);

    try {
      const params = new URLSearchParams({
        startDate,
        endDate,
        location,
        paymentType,
        orderType
      });

      const response = await fetch(`${API_URL}/api/admin/payments/report?${params}`, {
        headers: { 'x-username': user.UserName }
      });

      const data = await response.json();

      if (response.ok && data.success) {
        // Set payments and totals immediately so table renders first
        setPayments(data.payments);
        setTotals(data.totals);
        setChartsReady(false);

        // Defer chart calculations to next frame so table renders first
        setTimeout(() => {
          // Calculate counts by method for bar chart
          const countByMethod = {};
          data.payments.forEach(payment => {
            if (payment.isAdjustment) return; // exclude balance write-offs
            const method = payment.PaymentMethod || 'Other';
            countByMethod[method] = (countByMethod[method] || 0) + 1;
          });

          setTotals(prev => ({ ...prev, countByMethod }));

          // Calculate weekly data for line chart
          setWeeklyData(calculateWeeklyData(data.payments));
          setChartsReady(true);
        }, 0);
      } else {
        setReportError(data.error || 'Failed to generate report');
      }
    } catch (error) {
      setReportError('Network error: ' + error.message);
    } finally {
      setReportLoading(false);
    }
  };

  // Print report
  const handlePrint = () => {
    window.print();
  };

  // ==================== PRICE LIST FUNCTIONS ====================

  // Fetch repairs
  const fetchRepairs = async () => {
    setPriceLoading(true);
    setPriceError(null);
    try {
      const response = await fetch(`${API_URL}/api/admin/repairs`, {
        headers: { 'x-username': user.UserName }
      });
      const data = await response.json();
      if (data.success) {
        setRepairs(data.repairs);
      } else {
        setPriceError('Failed to fetch repairs');
      }
    } catch (error) {
      setPriceError('Network error: ' + error.message);
    } finally {
      setPriceLoading(false);
    }
  };

  // Fetch accessories
  const fetchAccessories = async () => {
    setPriceLoading(true);
    setPriceError(null);
    try {
      const response = await fetch(`${API_URL}/api/admin/accessories`, {
        headers: { 'x-username': user.UserName }
      });
      const data = await response.json();
      if (data.success) {
        setAccessories(data.accessories);
      } else {
        setPriceError('Failed to fetch accessories');
      }
    } catch (error) {
      setPriceError('Network error: ' + error.message);
    } finally {
      setPriceLoading(false);
    }
  };

  // Fetch wig pricing
  const fetchWigPricing = async () => {
    setPriceLoading(true);
    setPriceError(null);
    try {
      const response = await fetch(`${API_URL}/api/admin/wig-pricing`, {
        headers: { 'x-username': user.UserName }
      });
      const data = await response.json();
      if (data.success) {
        setWigPricing(data.pricing);
      } else {
        setPriceError('Failed to fetch wig pricing');
      }
    } catch (error) {
      setPriceError('Network error: ' + error.message);
    } finally {
      setPriceLoading(false);
    }
  };

  // Save repair
  const handleSaveRepair = async (repair, isNew = false) => {
    setPriceLoading(true);
    setPriceError(null);
    try {
      const url = isNew
        ? `${API_URL}/api/admin/repairs`
        : `${API_URL}/api/admin/repairs/${repair.ID}`;

      const response = await fetch(url, {
        method: isNew ? 'POST' : 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'x-username': user.UserName
        },
        body: JSON.stringify(repair)
      });

      const data = await response.json();
      if (data.success) {
        setPriceSuccess(isNew ? 'Repair added successfully' : 'Repair updated successfully');
        setEditingItem(null);
        setShowAddForm(false);
        fetchRepairs();
        setTimeout(() => setPriceSuccess(null), 3000);
      } else {
        setPriceError(data.error || 'Failed to save repair');
        setTimeout(() => setPriceError(null), 5000);
      }
    } catch (error) {
      setPriceError('Network error: ' + error.message);
      setTimeout(() => setPriceError(null), 5000);
    } finally {
      setPriceLoading(false);
    }
  };

  // Delete repair
  const handleDeleteRepair = async (id) => {
    if (!window.confirm('Are you sure you want to delete this repair option?')) return;

    setPriceLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/repairs/${id}`, {
        method: 'DELETE',
        headers: { 'x-username': user.UserName }
      });

      const data = await response.json();
      if (data.success) {
        setPriceSuccess('Repair deleted successfully');
        fetchRepairs();
        setTimeout(() => setPriceSuccess(null), 3000);
      } else {
        setPriceError(data.error || 'Failed to delete repair');
        setTimeout(() => setPriceError(null), 5000);
      }
    } catch (error) {
      setPriceError('Network error: ' + error.message);
      setTimeout(() => setPriceError(null), 5000);
    } finally {
      setPriceLoading(false);
    }
  };

  // Save accessory
  const handleSaveAccessory = async (accessory, isNew = false) => {
    setPriceLoading(true);
    setPriceError(null);
    try {
      const url = isNew
        ? `${API_URL}/api/admin/accessories`
        : `${API_URL}/api/admin/accessories/${accessory.ID}`;

      const response = await fetch(url, {
        method: isNew ? 'POST' : 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'x-username': user.UserName
        },
        body: JSON.stringify(accessory)
      });

      const data = await response.json();
      if (data.success) {
        setPriceSuccess(isNew ? 'Accessory added successfully' : 'Accessory updated successfully');
        setEditingItem(null);
        setShowAddForm(false);
        fetchAccessories();
        setTimeout(() => setPriceSuccess(null), 3000);
      } else {
        setPriceError(data.error || 'Failed to save accessory');
        setTimeout(() => setPriceError(null), 5000);
      }
    } catch (error) {
      setPriceError('Network error: ' + error.message);
      setTimeout(() => setPriceError(null), 5000);
    } finally {
      setPriceLoading(false);
    }
  };

  // Delete accessory
  const handleDeleteAccessory = async (id) => {
    if (!window.confirm('Are you sure you want to delete this accessory?')) return;

    setPriceLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/accessories/${id}`, {
        method: 'DELETE',
        headers: { 'x-username': user.UserName }
      });

      const data = await response.json();
      if (data.success) {
        setPriceSuccess('Accessory deleted successfully');
        fetchAccessories();
        setTimeout(() => setPriceSuccess(null), 3000);
      } else {
        setPriceError(data.error || 'Failed to delete accessory');
        setTimeout(() => setPriceError(null), 5000);
      }
    } catch (error) {
      setPriceError('Network error: ' + error.message);
      setTimeout(() => setPriceError(null), 5000);
    } finally {
      setPriceLoading(false);
    }
  };

  // Save wig pricing
  const handleSaveWigPricing = async (pricing, isNew = false) => {
    setPriceLoading(true);
    setPriceError(null);
    try {
      const url = isNew
        ? `${API_URL}/api/admin/wig-pricing`
        : `${API_URL}/api/admin/wig-pricing/${pricing.ID}`;

      const response = await fetch(url, {
        method: isNew ? 'POST' : 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'x-username': user.UserName
        },
        body: JSON.stringify(pricing)
      });

      const data = await response.json();
      if (data.success) {
        setPriceSuccess('Wig price updated successfully');
        setEditingItem(null);
        setShowAddForm(false);
        fetchWigPricing();
        setTimeout(() => setPriceSuccess(null), 3000);
      } else {
        setPriceError(data.error || 'Failed to save wig pricing');
        setTimeout(() => setPriceError(null), 5000);
      }
    } catch (error) {
      setPriceError('Network error: ' + error.message);
      setTimeout(() => setPriceError(null), 5000);
    } finally {
      setPriceLoading(false);
    }
  };

  // Delete wig pricing (not used but kept for potential future use)
  const handleDeleteWigPricing = async (id) => {
    if (!window.confirm('Are you sure you want to delete this wig pricing?')) return;

    setPriceLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/wig-pricing/${id}`, {
        method: 'DELETE',
        headers: { 'x-username': user.UserName }
      });

      const data = await response.json();
      if (data.success) {
        setPriceSuccess('Wig pricing deleted successfully');
        fetchWigPricing();
        setTimeout(() => setPriceSuccess(null), 3000);
      } else {
        setPriceError(data.error || 'Failed to delete wig pricing');
        setTimeout(() => setPriceError(null), 5000);
      }
    } catch (error) {
      setPriceError('Network error: ' + error.message);
      setTimeout(() => setPriceError(null), 5000);
    } finally {
      setPriceLoading(false);
    }
  };

  // Filter repairs by search and type
  const filteredRepairs = repairs.filter(r => {
    const matchesSearch = r.RepairDescription.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = filterType === 'All' || r.Type === filterType;
    return matchesSearch && matchesType;
  });

  // Filter accessories by search
  const filteredAccessories = accessories.filter(a =>
    a.Accessory_Description.toLowerCase().includes(searchTerm.toLowerCase()) ||
    a.Category.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Filter and sort wig pricing by search, then by length (short first, then numerical)
  const filteredWigPricing = wigPricing
    .filter(w => w.WigType.toLowerCase().includes(searchTerm.toLowerCase()))
    .sort((a, b) => {
      // First sort by WigType
      const typeCompare = a.WigType.localeCompare(b.WigType);
      if (typeCompare !== 0) return typeCompare;

      // Then sort by length: 'short' first, then numerical
      const aLength = a.Wig_Length?.toLowerCase() || '';
      const bLength = b.Wig_Length?.toLowerCase() || '';

      if (aLength === 'short' && bLength !== 'short') return -1;
      if (bLength === 'short' && aLength !== 'short') return 1;

      // Extract numbers for numerical comparison
      const aNum = parseInt(aLength) || 999;
      const bNum = parseInt(bLength) || 999;
      return aNum - bNum;
    });

  // Delete order handler
  const handleDeleteOrder = async (e) => {
    e.preventDefault();

    if (!orderNo.trim()) {
      setMessage({ type: 'error', text: 'Please enter an order number' });
      return;
    }

    // const confirmDelete = window.confirm(
    //   `Are you sure you want to DELETE order #${orderNo}?\n\nThis will permanently remove:\n- Order entry\n- All order items\n- All journals\n- All room tasks\n\nThis action cannot be undone!`
    // );

    // if (!confirmDelete) return;

    setLoading(true);
    setMessage(null);

    try {
      const response = await fetch(`${API_URL}/api/admin/order/${orderNo}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'x-username': user.UserName
        }
      });

      const data = await response.json();

      if (response.ok) {
        setMessage({
          type: 'success',
          text: `Order #${orderNo} deleted successfully!`,
          details: data.deletedRecords
        });
        setOrderNo('');
        setPreviewData(null);
      } else {
        setMessage({
          type: 'error',
          text: data.error || 'Failed to delete order'
        });
      }
    } catch (error) {
      setMessage({
        type: 'error',
        text: 'Network error: ' + error.message
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="system-admin-page">
      {/* Toast Notifications */}
      {(priceError || priceSuccess) && (
        <div className="toast-container">
          {priceError && <div className="toast error"><p>{priceError}</p></div>}
          {priceSuccess && <div className="toast success"><p>{priceSuccess}</p></div>}
        </div>
      )}
{/* <header className="admin-header">
        <h1>System Administration</h1>
        <p className="admin-subtitle">Restricted Access - {user.UserFullName}</p>
      </header> */}
      <div className="admin-container">
        {/* Left Sidebar */}
        <aside className="admin-sidebar">
          
          <h3>Actions</h3>
          <nav className="admin-tabs">
            <button
              className={`admin-tab ${activeTab === 'reports' ? 'active' : ''}`}
              onClick={() => setActiveTab('reports')}
            >
              Payment Reports
            </button>
            <button
              className={`admin-tab ${activeTab === 'deleteOrder' ? 'active' : ''}`}
              onClick={() => setActiveTab('deleteOrder')}
            >
              Delete Order
            </button>
            <button
              className={`admin-tab ${activeTab === 'users' ? 'active' : ''}`}
              onClick={() => setActiveTab('users')}
            >
              Manage Users
            </button>
            <button
              className={`admin-tab ${activeTab === 'workers' ? 'active' : ''}`}
              onClick={() => setActiveTab('workers')}
            >
              Manage Workers
            </button>
            <button
              className={`admin-tab ${activeTab === 'priceList' ? 'active' : ''}`}
              onClick={() => setActiveTab('priceList')}
            >
              Price List
            </button>
          </nav>
        </aside>

        {/* Main Content Area */}
        <main className="admin-main">
          <div className="admin-content">
            {/* Payment Reports Tab */}
            {activeTab === 'reports' && (
              <div className="admin-section">
                <h2>Payment Report</h2>

                {/* Filter Controls */}
                <div className="report-controls hidden-print">
                  <div className="control-row">
                    <div className="control-group">
                      <label>Quick Select:</label>
                      <select
                        value={datePreset}
                        onChange={(e) => handlePresetChange(e.target.value)}
                      >
                        {DATE_PRESETS.map(p => (
                          <option key={p.label} value={p.label}>{p.label}</option>
                        ))}
                      </select>
                    </div>

                    <div className="control-group">
                      <label>Start Date:</label>
                      <input
                        type="date"
                        value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}
                      />
                    </div>

                    <div className="control-group">
                      <label>End Date:</label>
                      <input
                        type="date"
                        value={endDate}
                        onChange={(e) => setEndDate(e.target.value)}
                      />
                    </div>

                    <div className="control-group">
                      <label>Location:</label>
                      <select
                        value={location}
                        onChange={(e) => setLocation(e.target.value)}
                      >
                        {LOCATIONS.map(loc => (
                          <option key={loc} value={loc}>{loc}</option>
                        ))}
                      </select>
                    </div>
                    <div className="control-group">
                      <label>Payment Type:</label>
                      <select
                        value={paymentType}
                        onChange={(e) => setPaymentType(e.target.value)}
                      >
                        {PAYMENT_TYPES.map(type => (
                          <option key={type} value={type}>{type}</option>
                        ))}
                      </select>
                    </div>

                     <div className="control-group">
                      <label>Order Type:</label>
                      <select
                        value={orderType}
                        onChange={(e) => setOrderType(e.target.value)}
                      >
                        {ORDER_TYPES.map(type => (
                          <option key={type} value={type}>{type}</option>
                        ))}
                      </select>
                    </div>

                    <div className="control-group">
                      <button
                        className="generate-btn"
                        onClick={handleGenerateReport}
                        disabled={reportLoading}
                      >
                        {reportLoading ? 'Loading...' : 'Generate Report'}
                      </button>
                    </div>

                    {payments.length > 0 && (
                      <div className="control-group">
                        <button className="print-btn" onClick={handlePrint}>
                          Print
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                {/* Print Header */}
                <div className="print-only print-header">
                  <h2>Payment Report</h2>
                  <p>{formatDisplayDate(startDate)} - {formatDisplayDate(endDate)}</p>
                  {location !== 'All' && <p>Location: {location}</p>}
                </div>

                {/* Error Display */}
                {reportError && (
                  <div className="message error">
                    <p>{reportError}</p>
                  </div>
                )}
           

                {/* Totals by Payment Method */}
                <div className="totals-summary">
                  <h3>Totals by Payment Method</h3>
                  <div className="totals-grid">
                    {Object.entries(totals.byMethod).map(([method, amount]) => (
                      <div key={method} className="total-item">
                        <span className="method-name">{method}:</span>
                        <span className="method-amount">{formatCurrency(amount)}</span>
                      </div>
                    ))}
                    <div className="total-item grand-total">
                      <span className="method-name">Grand Total (revenue):</span>
                      <span className="method-amount">{formatCurrency(totals.total)}</span>
                    </div>
                    {totals.adjustmentsCount > 0 && (
                      <div className="total-item adjustment-total">
                        <span className="method-name">
                          Adjustments — not revenue ({totals.adjustmentsCount}):
                        </span>
                        <span className="method-amount">{formatCurrency(totals.adjustmentsTotal)}</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Empty State */}
                {!reportLoading && payments.length === 0 && !reportError && (
                  <div className="empty-state">
                    <p>Select date range and click "Generate Report" to view payments.</p>
                  </div>
                )}

                {/* Report and Charts Layout - Table on left, Charts stacked on right */}
                {payments.length > 0 && (
                  <div className="report-layout">
                    {/* Left side - Report Table */}
                    <div className="report-table-container">
                      <table className="report-table">
                        <thead>
                          <tr>
                            <th>Order</th>
                            <th>Customer</th>
                            <th>Amount</th>
                            <th>Method</th>
                            <th>Type</th>
                            <th>Order Type</th>
                            <th>Date</th>
                            <th>Location</th>
                            <th>Entered By</th>
                          </tr>
                        </thead>
                        <tbody>
                          {payments.map((payment, index) => (
                            <tr key={index} className={payment.isAdjustment ? 'is-adjustment' : undefined}>
                              <td>
                                  <a
                                    href={`/work-order-profile/${payment.orderno}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="order-link-s"
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    {payment.orderno}
                                  </a>
                              </td>
                              <td>{payment.ordercustomername}</td>
                              <td className="amount">{formatCurrency(payment.PaymentAmount)}</td>
                              <td>{payment.PaymentMethod}</td>
                              <td>
                                {payment.PaymentType}
                                {payment.isAdjustment && (
                                  <span className="adjustment-badge">non-revenue</span>
                                )}
                              </td>
                              <td>{payment.OrderWigType}</td>
                              <td>{formatDisplayDate(payment.PaymentDate)}</td>
                              <td>{payment.orderWarehouse}</td>
                              <td>{payment.PaymentAcceptedBy}</td>
                            </tr>
                          ))}
                        </tbody>
                        <tfoot>
                          <tr className="total-row">
                            <td colSpan="2">
                              <strong>
                                Total ({payments.filter(p => !p.isAdjustment).length} payments
                                {totals.adjustmentsCount > 0
                                  ? `, ${totals.adjustmentsCount} adjustments excluded`
                                  : ''})
                              </strong>
                            </td>
                            <td className="amount"><strong>{formatCurrency(totals.total)}</strong></td>
                            <td colSpan="6"></td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>

                    {/* Right side - Charts stacked */}
                    <div className="charts-column">
                    {!chartsReady ? (
                      <div className="charts-loading">Loading charts...</div>
                    ) : (
                    <>
                    {/* CSS Bar Chart - Payment Method Counts */}
                    <div className="chart-container bar-chart-container">
                      <h3>Payments by Method</h3>
                      <div className="bar-chart">
                        {Object.entries(totals.countByMethod || {}).map(([method, count]) => {
                          const maxCount = Math.max(...Object.values(totals.countByMethod || {}), 1);
                          const percentage = (count / maxCount) * 100;
                          return (
                            <div key={method} className="bar-row">
                              <span className="bar-label">{method}</span>
                              <div className="bar-track">
                                <div
                                  className="bar-fill"
                                  style={{ width: `${percentage}%` }}
                                  data-method={method.toLowerCase().replace(/\s+/g, '-')}
                                />
                              </div>
                              <span className="bar-count">{count}</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    {/* Line Chart - Weekly Comparison */}
                    <div className="chart-container line-chart-container">
                      <h3>Weekly Comparison (M-F)</h3>
                      <div className="line-chart">
                        <div className="chart-area">
                          {/* Y-axis labels */}
                          <div className="y-axis">
                            {(() => {
                              const allValues = [...weeklyData.currentWeek, ...weeklyData.previousWeek];
                              const maxVal = Math.max(...allValues, 100);
                              const step = Math.ceil(maxVal / 4 / 100) * 100;
                              return [4, 3, 2, 1, 0].map(i => (
                                <span key={i} className="y-label">${(step * i / 1000).toFixed(1)}k</span>
                              ));
                            })()}
                          </div>

                          {/* Chart grid and lines */}
                          <div className="chart-grid">
                            {/* Grid lines */}
                            <div className="grid-lines">
                              {[0, 1, 2, 3, 4].map(i => (
                                <div key={i} className="grid-line" />
                              ))}
                            </div>

                            {/* Data points and lines */}
                            <svg className="line-svg" viewBox="0 0 100 100" preserveAspectRatio="none">
                              {(() => {
                                const allValues = [...weeklyData.currentWeek, ...weeklyData.previousWeek];
                                const maxVal = Math.max(...allValues, 100);

                                const currentPoints = weeklyData.currentWeek.map((val, i) => ({
                                  x: (i / 4) * 100,
                                  y: 100 - (val / maxVal) * 100
                                }));

                                const previousPoints = weeklyData.previousWeek.map((val, i) => ({
                                  x: (i / 4) * 100,
                                  y: 100 - (val / maxVal) * 100
                                }));

                                const currentPath = currentPoints.map((p, i) =>
                                  `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`
                                ).join(' ');

                                const previousPath = previousPoints.map((p, i) =>
                                  `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`
                                ).join(' ');

                                return (
                                  <>
                                    <path className="line-previous" d={previousPath} />
                                    <path className="line-current" d={currentPath} />
                                    {currentPoints.map((p, i) => (
                                      <circle key={`curr-${i}`} className="point-current" cx={p.x} cy={p.y} r="2" />
                                    ))}
                                    {previousPoints.map((p, i) => (
                                      <circle key={`prev-${i}`} className="point-previous" cx={p.x} cy={p.y} r="2" />
                                    ))}
                                  </>
                                );
                              })()}
                            </svg>
                          </div>
                        </div>

                        {/* X-axis labels */}
                        <div className="x-axis">
                          {['Mon', 'Tue', 'Wed', 'Thu', 'Fri'].map(day => (
                            <span key={day} className="x-label">{day}</span>
                          ))}
                        </div>

                        {/* Legend */}
                        <div className="chart-legend">
                          <span className="legend-item current">
                            <span className="legend-color"></span> Current Week
                          </span>
                          <span className="legend-item previous">
                            <span className="legend-color"></span> Previous Week
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Pie Chart - Order Type Breakdown */}
                    <div className="chart-container pie-chart-container">
                      <h3>Orders by Type</h3>
                      <div className="pie-chart">
                        {(() => {
                          const orderTypes = totals.byOrderType || {};
                          const entries = Object.entries(orderTypes);
                          const totalCount = entries.reduce((sum, [, data]) => sum + data.count, 0);

                          if (totalCount === 0) return <p className="empty-chart">No data</p>;

                          // Colors for pie slices
                          const colors = ['#007bff', '#28a745', '#ffc107', '#dc3545', '#6f42c1', '#17a2b8'];

                          // Calculate pie slices
                          let cumulativePercent = 0;
                          const slices = entries.map(([type, data], index) => {
                            const percent = (data.count / totalCount) * 100;
                            const startAngle = cumulativePercent * 3.6; // 360/100
                            cumulativePercent += percent;
                            const endAngle = cumulativePercent * 3.6;

                            // Convert to radians for SVG arc
                            const startRad = (startAngle - 90) * (Math.PI / 180);
                            const endRad = (endAngle - 90) * (Math.PI / 180);

                            const x1 = 50 + 40 * Math.cos(startRad);
                            const y1 = 50 + 40 * Math.sin(startRad);
                            const x2 = 50 + 40 * Math.cos(endRad);
                            const y2 = 50 + 40 * Math.sin(endRad);

                            const largeArc = percent > 50 ? 1 : 0;

                            const pathD = percent >= 100
                              ? `M 50 10 A 40 40 0 1 1 49.99 10 Z`
                              : `M 50 50 L ${x1} ${y1} A 40 40 0 ${largeArc} 1 ${x2} ${y2} Z`;

                            return {
                              type,
                              count: data.count,
                              amount: data.amount,
                              percent,
                              color: colors[index % colors.length],
                              pathD
                            };
                          });

                            return (
                            <>
                              <svg viewBox="0 0 100 100" className="pie-svg">
                              {slices.map((slice, i) => (
                                <path
                                key={i}
                                d={slice.pathD}
                                fill={slice.color}
                                className="pie-slice"
                                data-tooltip={`${slice.type}: ${slice.count} orders (${slice.percent.toFixed(1)}%)`}
                                >
                                <title>{slice.type}: {slice.count} orders ({slice.percent.toFixed(1)}%) - {formatCurrency(slice.amount)}</title>
                                </path>
                              ))}
                              </svg>
                              <div className="pie-legend">
                              {slices.map((slice, i) => (
                                <div key={i} className="pie-legend-item">
                                <span className="pie-color" style={{ background: slice.color }}></span>
                                <span className="pie-label">{slice.type}</span>
                                <span className="pie-value">{slice.count} ({slice.percent.toFixed(0)}%)</span>
                                </div>
                              ))}
                              </div>
                            </>
                            );
                          })()}
                          </div>
                        </div>
                        </>
                    )}
                        </div>
                        </div>
                      )}
                      </div>
                    )}
                    

                    {/* Delete Order Tab */}
                    {activeTab === 'deleteOrder' && (
                      <div className="admin-section">
                      <h2>Delete Work Order</h2>
                      <p className="warning-text">
                        Warning: This action permanently deletes an order and all related records.
                        This cannot be undone.
                      </p>

                      <form onSubmit={(e) => { e.preventDefault(); handlePreviewOrder(); }} className="delete-order-form">
                        <div className="form-group">
                        <label htmlFor="orderNo">Order Number</label>
                        <input
                          type="number"
                          id="orderNo"
                          value={orderNo}
                          onChange={(e) => setOrderNo(e.target.value)}
                          placeholder="Enter order number (e.g., 54212)"
                          disabled={loading}
                          className='search-input'
                        />
                        </div>

                        <button
                        type="submit"
                        className="button-52"
                        disabled={loading || !orderNo.trim()}
                        >
                        {loading ? 'Loading...' : 'Preview Order'}
                        </button>
                      </form>

                      {message && (
                        <div className={`message ${message.type}`}>
                        <p>{message.text}</p>
                        {message.details && (
                          <ul className="deletion-details">
                          <li>Payment Records deleted: {message.details.journals}</li>
                          <li>Line Items deleted: {message.details.items}</li>
                          <li>Order entry deleted: {message.details.entry}</li>
                          <li>Room tasks deleted: {message.details.tasks}</li>
                          </ul>
                        )}
                        </div>
                      )}
                      {/* Preview Data Display */}
{previewData && (
  <div className="preview-data">
    <h3>Order Details</h3>
    
    {previewData.order && (
      <div className="preview-section">
        <h4>Order Information</h4>
        <table className="preview-table">
          <tbody>
            <tr>
              <td><strong>Order No:</strong></td>
              <td>{previewData.order.OrderNo}</td>
            </tr>
            <tr>
              <td><strong>Customer Name:</strong></td>
              <td>{previewData.order.CustomerFullName}</td>
            </tr>
            <tr>
              <td><strong>Order Date:</strong></td>
              <td>{new Date(previewData.order.OrderDate).toLocaleDateString()}</td>
            </tr>
            <tr>
              <td><strong>Order Status:</strong></td>
              <td>{previewData.order.OrderStatus}</td>
            </tr>
            <tr>
              <td><strong>Total Amount:</strong></td>
              <td>{formatCurrency(
            (previewData.items || []).reduce((sum, item) => sum + (item.OrderQty * item.OrderItemRetailCost), 0)
          )}</td>
            </tr>
          </tbody>
        </table>
      </div>
    )}

    {previewData.items && previewData.items.length > 0 && (
      <div className="preview-section">
        <h4>Order Items ({previewData.items.length})</h4>
        <table className="preview-table">
          <thead>
            <tr>
              <th>Item</th>
              <th>Quantity</th>
              <th>Price</th>
              <th>Total</th>
            </tr>
          </thead>
          <tbody>
            {previewData.items.map((item, idx) => (
              <tr key={idx}>
                <td>{item.OrderItemDescription}</td>
                <td>{item.OrderQty}</td> 
                <td>{formatCurrency(item.OrderItemRetailCost)}</td>
                <td>{formatCurrency(item.OrderQty * item.OrderItemRetailCost)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}

    {previewData.journals && previewData.journals.length > 0 && (
      <div className="preview-section">
        <h4>Journals ({previewData.journals.length})</h4>
        <table className="preview-table">
          <thead>
            <tr>
              <th>Date</th>
              <th>Entry</th>
              <th>Amount</th>
              <th>Entered By</th>
            </tr>
          </thead>
          <tbody>
            {previewData.journals.map((journal, idx) => (
              <tr key={idx}>
                <td>{formatDisplayDate(journal.PaymentDate)}</td>
                <td>{journal.PaymentType}</td>
                <td>{formatCurrency(journal.PaymentAmount)}</td>
                <td>{journal.PaymentAcceptedBy}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}

    {previewData.tasks && previewData.tasks.length > 0 && (
      <div className="preview-section">
        <h4>Room Tasks ({previewData.tasks.length})</h4>
        <table className="preview-table">
          <thead>
            <tr>
              <th>RoomNo</th>
              <th>RoomTask</th>
              <th>Status</th>
              <th>Worker</th>
            </tr>
          </thead>
          <tbody>
            {previewData.tasks.map((task, idx) => (
              <tr key={idx}>
                <td>{task.RoomNo}</td>
                <td>{task.RoomTask}</td>
                <td>{task.TaskStatus}</td>
                <td>{task.AssignedWorker}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}

    <div className="preview-actions">
      <button 
        className="confirm-delete-btn"
      >
        <span class="delete-text"
         onClick={handleDeleteOrder}
         >CONFIRM DELETE</span>
        <svg class="delete-icon" xmlns="http://www.w3.org/2000/svg" width="25" height="25" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
          <path stroke-linecap="round" stroke-linejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
        </svg>
      </button>

      
      
    </div>
  </div>
)}
                      </div>
                    )}

            {/* User Management Tab */}
            {activeTab === 'users' && (
              <div className="admin-section user-management-section">
                <h2>User Management</h2>

                {/* Toast notifications for users */}
                {(usersError || usersSuccess) && (
                  <div className="toast-container">
                    {usersError && <div className="toast error"><p>{usersError}</p></div>}
                    {usersSuccess && <div className="toast success"><p>{usersSuccess}</p></div>}
                  </div>
                )}

                {/* Search and Filter Controls */}
                <div className="price-controls">
                  <input
                    type="text"
                    placeholder="Search by name, username, or email..."
                    value={userSearchTerm}
                    onChange={(e) => setUserSearchTerm(e.target.value)}
                    className="search-input"
                    style={{ minWidth: '280px' }}
                  />
                  <select
                    value={userLocationFilter}
                    onChange={(e) => setUserLocationFilter(e.target.value)}
                    className="filter-bubble"
                  >
                    <option value="All">All Locations</option>
                    {USER_LOCATIONS.map(loc => (
                      <option key={loc} value={loc}>{loc}</option>
                    ))}
                  </select>
                  <button
                    className="add-price-btn"
                    onClick={() => { setShowAddUserForm(true); setEditingUser(null); }}
                  >
                    + Add New User
                  </button>
                </div>

                {usersLoading && <div className="loading">Loading users...</div>}

                {/* Add New User Form */}
                {showAddUserForm && (
                  <div className="price-form" style={{ marginBottom: '1.5rem' }}>
                    <h4>Add New User</h4>
                    <form onSubmit={(e) => {
                      e.preventDefault();
                      const formData = new FormData(e.target);
                      const firstName = formData.get('firstname') || '';
                      const lastName = formData.get('lastname') || '';
                      const fullName = `${firstName} ${lastName}`.trim();
                      handleSaveUser({
                        UserName: formData.get('username'),
                        UserPassword: formData.get('password'),
                        UserFullName: fullName || formData.get('username'),
                        UserFirstName: firstName,
                        UserLastName: lastName,
                        UserEmail: formData.get('email'),
                        UserPhoneNum: formData.get('phone'),
                        UserJobTitle: formData.get('jobtitle'),
                        UserLocation: formData.get('location'),
                        CountryCode: formData.get('countrycode'),
                        IsManagement: formData.get('ismanagement') === 'on' ? 1 : 0,
                        IsDistributors: formData.get('isdistributors') === 'on' ? 1 : 0,
                        ActiveStatus: 1
                      }, true);
                    }}>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.75rem' }}>
                        <input name="username" placeholder="Username *" required autoComplete="off" />
                        <input name="password" type="password" placeholder="Password *" required autoComplete="new-password" />
                        <input name="firstname" placeholder="First Name *" required />
                        <input name="lastname" placeholder="Last Name *" required />
                        <input name="email" type="email" placeholder="Email" />
                        <input name="phone" placeholder="Phone Number" />
                        <input name="jobtitle" placeholder="Job Title" />
                        <select name="location" required>
                          <option value="">Select Location *</option>
                          {USER_LOCATIONS.map(loc => (
                            <option key={loc} value={loc}>{loc}</option>
                          ))}
                        </select>
                        <select name="countrycode" required>
                          {COUNTRY_CODES.map(cc => (
                            <option key={cc.value} value={cc.value}>{cc.label}</option>
                          ))}
                        </select>
                        <label style={{ display: 'flex', alignItems: 'left', padding: '0.5rem' }}>
                          <input name="ismanagement" type="checkbox"
                          style={{ cursor: 'pointer', width: '20px', height: '20px' }} />
                          Management/Admin
                        </label>
                        <label style={{ display: 'flex', alignItems: 'left', padding: '0.5rem' }}>
                          <input name="isdistributors" type="checkbox"
                          style={{ cursor: 'pointer', width: '20px', height: '20px' }} />
                          Send Out
                        </label>
                      </div>
                      <div className="form-actions" style={{ marginTop: '1rem' }}>
                        <button type="submit">Create User</button>
                        <button type="button" onClick={() => setShowAddUserForm(false)}>Cancel</button>
                      </div>
                    </form>
                  </div>
                )}

                {/* Users Table — always read-only; editing opens a side drawer */}
                {!usersLoading && (
                  <div className="users-table-wrapper">
                    <table className="users-table">
                      <thead>
                        <tr>
                          <th>Username</th>
                          <th className="hide-mobile">First Name</th>
                          <th className="hide-mobile">Last Name</th>
                          <th className="hide-mobile">Job Title</th>
                          <th className="hide-mobile">Email</th>
                          <th className="hide-mobile">Phone</th>
                          <th className="hide-mobile">Location</th>
                          <th>Send Out</th>
                          <th className="hide-mobile">Country</th>
                          <th>Admin</th>
                          <th><i className="fas fa-bell"></i></th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredUsers.length === 0 ? (
                          <tr>
                            <td colSpan="12" style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-primary)' }}>
                              {users.length === 0 ? 'No users found. Click "Add New User" to create one.' : 'No users match your search criteria.'}
                            </td>
                          </tr>
                        ) : (
                          filteredUsers.map((u) => (
                            <tr key={u.UserID} className={editingUser?.UserID === u.UserID ? 'user-row-editing' : ''}>
                              <td><strong>{u.UserName}</strong></td>
                              <td className="hide-mobile">{u.UserFirstName}</td>
                              <td className="hide-mobile">{u.UserLastName}</td>
                              <td className="hide-mobile">{u.UserJobTitle}</td>
                              <td className="hide-mobile">{u.UserEmail}</td>
                              <td className="hide-mobile">{u.UserPhoneNum}</td>
                              <td className="hide-mobile">
                                <span className={`type-badge ${(u.UserLocation || '').toLowerCase()}`}>
                                  {u.UserLocation || 'N/A'}
                                </span>
                              </td>
                              <td>
                                {(u.IsDistributors === 1 || u.IsDistributors === true) ? (
                                  <span style={{ color: 'var(--success)', fontWeight: 'bold' }}><i className="fa fa-check" aria-hidden="true"></i></span>
                                ) : (
                                  <span style={{ color: 'var(--text-primary)' }}><i className="fa fa-times" aria-hidden="true"></i></span>
                                )}
                              </td>
                              <td className="hide-mobile">{COUNTRY_CODES.find(cc => cc.value === u.CountryCode)?.label || u.CountryCode || 'USA'}</td>
                              <td>
                                {(u.IsManagement === 1 || u.IsManagement === true) ? (
                                  <span style={{ color: 'var(--success)', fontWeight: 'bold' }}><i className="fa fa-check" aria-hidden="true"></i></span>
                                ) : (
                                  <span style={{ color: 'var(--text-primary)' }}><i className="fa fa-times" aria-hidden="true"></i></span>
                                )}
                              </td>
                              <td>
                                {(u.ActiveStatus === 1 || u.ActiveStatus === true) ? (
                                  <span style={{ color: 'var(--success)', fontWeight: 'bold' }}><i className="fa fa-check" aria-hidden="true"></i></span>
                                ) : (
                                  <span style={{ color: 'var(--text-primary)' }}><i className="fa fa-times" aria-hidden="true"></i></span>
                                )}
                              </td>
                              <td>
                                <button type="button" onClick={(e) => { e.stopPropagation(); setEditingUser({ ...u, NewPassword: u.UserPassword || '' }); setShowPassword(false); }}>Edit</button>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                    <div style={{ marginTop: '1rem', color: 'var(--text-primary)', fontSize: '1.2em' }}>
                      Showing {filteredUsers.length} of {users.length} users
                    </div>
                  </div>
                )}

                {/* Edit User Drawer */}
                {editingUser && (
                  <>
                    <div className="user-edit-overlay" onClick={() => setEditingUser(null)} />
                    <div className="user-edit-drawer">
                      <div className="user-edit-drawer-header">
                        <h3>Edit User — {editingUser.UserName}</h3>
                        <button className="user-edit-close" onClick={() => setEditingUser(null)} title="Close">
                          <i className="fas fa-times" />
                        </button>
                      </div>
                      <div className="user-edit-body">
                        <div className="user-edit-grid">
                          <div className="user-edit-field">
                            <label>Username</label>
                            <input value={editingUser.UserName} onChange={(e) => setEditingUser({ ...editingUser, UserName: e.target.value })} />
                          </div>
                          <div className="user-edit-field">
                            <label>Job Title</label>
                            <input value={editingUser.UserJobTitle || ''} onChange={(e) => setEditingUser({ ...editingUser, UserJobTitle: e.target.value })} />
                          </div>
                          <div className="user-edit-field">
                            <label>First Name</label>
                            <input value={editingUser.UserFirstName || ''} onChange={(e) => setEditingUser({ ...editingUser, UserFirstName: e.target.value })} />
                          </div>
                          <div className="user-edit-field">
                            <label>Last Name</label>
                            <input value={editingUser.UserLastName || ''} onChange={(e) => setEditingUser({ ...editingUser, UserLastName: e.target.value })} />
                          </div>
                          <div className="user-edit-field">
                            <label>Email</label>
                            <input type="email" value={editingUser.UserEmail || ''} onChange={(e) => setEditingUser({ ...editingUser, UserEmail: e.target.value })} />
                          </div>
                          <div className="user-edit-field">
                            <label>Phone</label>
                            <input value={editingUser.UserPhoneNum || ''} onChange={(e) => setEditingUser({ ...editingUser, UserPhoneNum: e.target.value })} />
                          </div>
                          <div className="user-edit-field">
                            <label>Location</label>
                            <select value={editingUser.UserLocation || ''} onChange={(e) => setEditingUser({ ...editingUser, UserLocation: e.target.value })}>
                              <option value="">Select</option>
                              {USER_LOCATIONS.map(loc => <option key={loc} value={loc}>{loc}</option>)}
                            </select>
                          </div>
                          <div className="user-edit-field">
                            <label>Country</label>
                            <select value={editingUser.CountryCode || 'US'} onChange={(e) => setEditingUser({ ...editingUser, CountryCode: e.target.value })}>
                              {COUNTRY_CODES.map(cc => <option key={cc.value} value={cc.value}>{cc.label}</option>)}
                            </select>
                          </div>
                          <div className="user-edit-field" style={{ gridColumn: '1 / -1' }}>
                            <label>New Password <span style={{ fontWeight: 400, textTransform: 'none', opacity: 0.6 }}>(leave blank to keep current)</span></label>
                            <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                              <input
                                type={showPassword ? 'text' : 'password'}
                                value={editingUser.NewPassword || ''}
                                onChange={(e) => setEditingUser({ ...editingUser, NewPassword: e.target.value })}
                                placeholder="Enter new password"
                                style={{ paddingRight: '40px' }}
                              />
                              <button
                                type="button"
                                onClick={() => setShowPassword(!showPassword)}
                                className="pw-toggle-btn"
                                title={showPassword ? 'Hide password' : 'Show password'}
                              >
                                <i className={`fas ${showPassword ? 'fa-eye-slash' : 'fa-eye'}`} />
                              </button>
                            </div>
                          </div>
                        </div>

                        <div className="user-edit-toggles">
                          <label className="user-edit-toggle">
                            <input
                              type="checkbox"
                              checked={editingUser.IsManagement === 1 || editingUser.IsManagement === true}
                              onChange={(e) => setEditingUser({ ...editingUser, IsManagement: e.target.checked ? 1 : 0 })}
                            />
                            <span>Admin / Management</span>
                          </label>
                          <label className="user-edit-toggle">
                            <input
                              type="checkbox"
                              checked={editingUser.IsDistributors === 1 || editingUser.IsDistributors === true}
                              onChange={(e) => setEditingUser({ ...editingUser, IsDistributors: e.target.checked ? 1 : 0 })}
                            />
                            <span>Send Out</span>
                          </label>
                          <label className="user-edit-toggle">
                            <input
                              type="checkbox"
                              checked={editingUser.ActiveStatus === 1 || editingUser.ActiveStatus === true}
                              onChange={(e) => setEditingUser({ ...editingUser, ActiveStatus: e.target.checked ? 1 : 0 })}
                            />
                            <span><i className="fas fa-bell" style={{ marginRight: '5px' }}></i>Reminders List</span>
                          </label>
                        </div>
                             <button
                          className="confirm-delete-btn"
                          onBlur={(e) => e.currentTarget.blur()}
                          title="Delete user"
                        >
                          <span
                            className="delete-text"
                            onClick={() => { handleDeleteUser(editingUser.UserID, editingUser.UserName); setEditingUser(null); }}
                          >
                            CONFIRM DELETE
                          </span>
                          <svg className="delete-icon" xmlns="http://www.w3.org/2000/svg" width="25" height="25" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                      <div className="user-edit-footer">
                        <button
                          className="user-edit-save"
                          onClick={() => {
                            const firstName = editingUser.UserFirstName || '';
                            const lastName = editingUser.UserLastName || '';
                            const fullName = `${firstName} ${lastName}`.trim() || editingUser.UserName;
                            handleSaveUser({ ...editingUser, UserFullName: fullName, UserPassword: editingUser.NewPassword || undefined });
                          }}
                        >
                          Save Changes
                        </button>
                        <button className="user-edit-cancel" onClick={() => setEditingUser(null)}>Cancel</button>
                   
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}

            {/* Manage Workers Tab */}
            {activeTab === 'workers' && (
              <div className="admin-section user-management-section">
                <h2>Manage Room Worker Assignments</h2>

                {/* Toast notifications */}
                {(workersError || workersSuccess) && (
                  <div className="toast-container">
                    {workersError && <div className="toast error"><p>{workersError}</p></div>}
                    {workersSuccess && <div className="toast success"><p>{workersSuccess}</p></div>}
                  </div>
                )}

                {/* Breadcrumb / Navigation */}
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  marginBottom: '1.5rem',
                  padding: '1rem',
                  background: 'var(--dark-elevated)',
                  borderRadius: '8px'
                }}>
                  <button
                    onClick={() => { setSelectedWorkerLocation(null); setSelectedWorkerRoom(null); setRoomWorkers([]); }}
                    style={{
                      background: selectedWorkerLocation ? 'transparent' : '#007bff',
                      color: selectedWorkerLocation ? 'var(--text-secondary)' : 'white',
                      border: 'none',
                      padding: '0.5rem 1rem',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontWeight: selectedWorkerLocation ? 'normal' : 'bold'
                    }}
                  >
                    Location
                  </button>
                  {selectedWorkerLocation && (
                    <>
                      <span style={{ color: 'var(--text-secondary)' }}>/</span>
                      <button
                        onClick={() => { setSelectedWorkerRoom(null); setRoomWorkers([]); }}
                        style={{
                          background: selectedWorkerRoom ? 'transparent' : '#007bff',
                          color: selectedWorkerRoom ? 'var(--text-secondary)' : 'white',
                          border: 'none',
                          padding: '0.5rem 1rem',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontWeight: selectedWorkerRoom ? 'normal' : 'bold'
                        }}
                      >
                        {selectedWorkerLocation}
                      </button>
                    </>
                  )}
                  {selectedWorkerRoom && (
                    <>
                      <span style={{ color: 'var(--text-secondary)' }}>/</span>
                      <span style={{
                        background: '#007bff',
                        color: 'white',
                        padding: '0.5rem 1rem',
                        borderRadius: '4px',
                        fontWeight: 'bold'
                      }}>
                        Room {selectedWorkerRoom}
                      </span>
                    </>
                  )}
                </div>

                {workersLoading && <div className="loading">Loading...</div>}

                {/* Step 1: Select Location OR Manage All Workers */}
                {!selectedWorkerLocation && !workersLoading && (
                  <>
                       {/* Select Location */}
                    <div className="worker-selection" style={{ marginBottom: '1rem' }}>
                      <h3 style={{ marginBottom: '1rem', color: 'var(--text-primary)' }}>Select Location to Assign Workers</h3>
                      <div className="worker-grid" style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                        gap: '1rem'
                      }}>
                        {roomLocations.map((location) => (
                          <button
                            key={location}
                            onClick={() => setSelectedWorkerLocation(location)}
                            className="worker-btn"
                          >
                            {location}
                          </button>
                        ))}
                        {roomLocations.length === 0 && (
                          <div style={{ gridColumn: '1 / -1', textAlign: 'center', color: 'var(--text-secondary)', padding: '2rem' }}>
                            No locations found. Please check room configuration.
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Manage All Workers Section */}
                    <div style={{
                      background: 'var(--dark-elevated)',
                      borderRadius: '8px',
                      padding: '1.5rem',
                      marginBottom: '2rem',
                      border: '1px solid #1874d1dc'
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                        <h3 style={{ margin: 0, color: 'var(--text-primary)' }}>All Workers ({Object.keys(allWorkers).length})</h3>
                        {/* <button
                          className="add-price-btn"
                          onClick={() => setShowAddNewWorker(!showAddNewWorker)}
                        >
                          {showAddNewWorker ? 'Cancel' : '+ Add New Worker'}
                        </button> */}
                      </div>

                      {/* Add New Worker Form */}
                      {/* {showAddNewWorker && (
                        <div style={{
                          display: 'flex',
                          gap: '1rem',
                          alignItems: 'flex-end',
                          marginBottom: '1rem',
                          padding: '1rem',
                          background: 'var(--dark-surface)',
                          borderRadius: '6px'
                        }}>
                          <div style={{ flex: 1 }}>
                            <label style={{ display: 'block', marginBottom: '0.5rem', color: 'var(--text-secondary)', fontSize: '0.9rem' }}>
                              Worker Name
                            </label>
                            <input
                              type="text"
                              value={newWorkerName}
                              onChange={(e) => setNewWorkerName(e.target.value)}
                              placeholder="Enter worker name..."
                              style={{
                                width: '100%',
                                padding: '0.75rem',
                                border: '1px solid var(--border-color)',
                                borderRadius: '4px',
                                background: 'var(--dark-elevated)',
                                color: 'var(--text-primary)'
                              }}
                            />
                          </div>
                          <p style={{ color: 'var(--text-secondary)', fontSize: '0.85rem', margin: 0 }}>
                            Select a location and room below to add this worker
                          </p>
                        </div>
                      )} */}

                      {/* Workers Grid */}
                      <div className="worker-grid" style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                        gap: '0.75rem',
                        maxHeight: '300px',
                        overflowY: 'auto'
                      }}>
                        {Object.entries(allWorkers).sort(([a], [b]) => a.localeCompare(b)).map(([workerName, workerRooms]) => (
                          <div
                            key={workerName}
                            style={{
                              padding: '0.75rem 1rem',
                              background: 'var(--dark-surface)',
                              border: '1px solid #1874d1dc',
                              borderRadius: '6px',
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              position: 'relative',
                              cursor: 'default'
                            }}
                            title={`Assigned to:\n${workerRooms.map(r => `• Room ${r.roomNo} - ${r.roomDescription} (${r.roomLocation})`).join('\n')}`}
                          >
                            <div>
                              <span style={{ color: 'var(--text-primary)', fontWeight: '500' }}>{workerName}</span>
                              <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                                {workerRooms.length} room{workerRooms.length !== 1 ? 's' : ''}
                              </div>
                            </div>
                            <button
                              onClick={() => handleRemoveWorkerFromAllRooms(workerName)}
                              className="delete-btn"
                              style={{ padding: '0.25rem 0.5rem', fontSize: '0.75rem' }}
                              title="Remove from all rooms"
                            >
                              X
                            </button>
                          </div>
                        ))}
                        {Object.keys(allWorkers).length === 0 && (
                          <div style={{ gridColumn: '1 / -1', textAlign: 'center', color: 'var(--text-secondary)', padding: '1rem' }}>
                            No workers found.
                          </div>
                        )}
                      </div>
                    </div>

               
                  </>
                )}

                {/* Step 2: Select Room */}
                {selectedWorkerLocation && !selectedWorkerRoom && !workersLoading && (
                  <div className="worker-selection" style={{ marginTop: '1rem' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                      <h3 style={{ margin: 0, color: 'var(--text-primary)' }}>Select Room in {selectedWorkerLocation}</h3>
                      <button
                        className="add-price-btn"
                        onClick={() => setShowAddRoomForm(!showAddRoomForm)}
                      >
                        {showAddRoomForm ? 'Cancel' : '+ Add Room'}
                      </button>
                    </div>

                    {/* Add Room Form */}
                    {showAddRoomForm && (
                      <div style={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: '1rem',
                        alignItems: 'flex-end',
                        marginBottom: '1.5rem',
                        padding: '1rem',
                        background: 'var(--dark-elevated)',
                        borderRadius: '8px',
                        border: '1px solid var(--border-color)'
                      }}>
                        <div style={{ flex: '0 1 140px' }}>
                          <label style={{ display: 'block', marginBottom: '0.5rem', color: 'var(--text-secondary)', fontSize: '0.9rem' }}>
                            Room Number
                          </label>
                          <input
                            type="text"
                            inputMode="numeric"
                            value={newRoom.RoomNo}
                            onChange={(e) => setNewRoom({ ...newRoom, RoomNo: e.target.value.replace(/\D/g, '') })}
                            placeholder="e.g. 9005"
                            className="room-input"
                           
                          />
                        </div>
                        <div style={{ flex: '1 1 220px' }}>
                          <label style={{ display: 'block', marginBottom: '0.5rem', color: 'var(--text-secondary)', fontSize: '0.9rem' }}>
                            Room Name
                          </label>
                          <input
                            type="text"
                            value={newRoom.RoomDescription}
                            onChange={(e) => setNewRoom({ ...newRoom, RoomDescription: e.target.value })}
                            onKeyDown={(e) => { if (e.key === 'Enter') handleAddRoom(); }}
                            placeholder="Enter room name..."
                            className="room-input"
                           
                          />
                        </div>
                        <div style={{ flex: '0 1 220px' }}>
                          <label style={{ display: 'block', marginBottom: '0.5rem', color: 'var(--text-secondary)', fontSize: '0.9rem' }}>
                            Parent Room (optional)
                          </label>
                          <select
                            value={newRoom.ParentRoomNo}
                            onChange={(e) => setNewRoom({ ...newRoom, ParentRoomNo: e.target.value })}
                            className="room-input"
                          >
                            <option value="">None</option>
                            {filteredRooms.map(r => (
                              <option key={r.RoomNo} value={r.RoomNo}>{r.RoomNo} - {r.RoomDescription}</option>
                            ))}
                          </select>
                        </div>
                        <button
                          onClick={handleAddRoom}
                          disabled={!newRoom.RoomNo.trim() || !newRoom.RoomDescription.trim()}
                          className="add-price-btn"
                          style={{ padding: '0.75rem 1.5rem' }}
                        >
                          Save Room
                        </button>
                      </div>
                    )}

                    <div className="worker-grid" style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))',
                      gap: '1rem'
                    }}>
                      {filteredRooms.map((room) => (
                        editingRoomNo === room.RoomNo ? (
                          <div key={room.RoomNo} className="worker-btn" style={{ cursor: 'default' }}>
                            <div style={{ fontWeight: 'bold', marginBottom: '0.5rem' }}>Room {room.RoomNo}</div>
                            <input
                              type="text"
                              value={editingRoomName}
                              onChange={(e) => setEditingRoomName(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') handleRenameRoom(room.RoomNo);
                                if (e.key === 'Escape') setEditingRoomNo(null);
                              }}
                              autoFocus
                              className="room-input"
                              style={{ marginBottom: '0.5rem' }}
                            />
                            <div style={{ display: 'flex', gap: '0.5rem' }}>
                              <button
                                className="add-price-btn"
                                onClick={() => handleRenameRoom(room.RoomNo)}
                                disabled={!editingRoomName.trim()}
                                style={{ padding: '0.25rem 0.75rem', fontSize: '0.8rem' }}
                              >
                                Save
                              </button>
                              <button
                                className="delete-btn"
                                onClick={() => setEditingRoomNo(null)}
                                style={{ padding: '0.25rem 0.75rem', fontSize: '0.8rem' }}
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div
                            key={room.RoomNo}
                            role="button"
                            tabIndex={0}
                            onClick={() => setSelectedWorkerRoom(room.RoomNo)}
                            onKeyDown={(e) => { if (e.key === 'Enter') setSelectedWorkerRoom(room.RoomNo); }}
                            className="worker-btn"
                            style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '0.5rem' }}
                          >
                            <div>
                              <div style={{ fontWeight: 'bold', marginBottom: '0.25rem' }}>Room {room.RoomNo}</div>
                              <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>{room.RoomDescription}</div>
                            </div>
                            <div style={{ display: 'flex', gap: '0.25rem', flexShrink: 0 }}>
                              <button
                                className="add-price-btn"
                                title="Rename room"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setEditingRoomNo(room.RoomNo);
                                  setEditingRoomName(room.RoomDescription || '');
                                }}
                                style={{ padding: '0.25rem 0.5rem', fontSize: '0.75rem' }}
                              >
                                Rename
                              </button>
                              <button
                                className="delete-btn"
                                title="Delete room"
                                onClick={(e) => { e.stopPropagation(); handleDeleteRoom(room); }}
                                style={{ padding: '0.25rem 0.5rem', fontSize: '0.75rem' }}
                              >
                                X
                              </button>
                            </div>
                          </div>
                        )
                      ))}
                      {filteredRooms.length === 0 && (
                        <div style={{ gridColumn: '1 / -1', textAlign: 'center', color: 'var(--text-secondary)', padding: '2rem' }}>
                          No rooms found for {selectedWorkerLocation}.
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Step 3: Show Workers for Selected Room */}
                {selectedWorkerRoom && !workersLoading && (
                  <div style={{ marginTop: '1rem' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                      <h3 style={{ color: 'var(--text-primary)', margin: 0 }}>
                        Workers in Room {selectedWorkerRoom}
                        {selectedRoomDetails && <span style={{ fontWeight: 'normal', color: 'var(--text-secondary)' }}> - {selectedRoomDetails.RoomDescription}</span>}
                      </h3>
                      <button
                        className="add-price-btn"
                        onClick={() => setShowAddWorkerForm(!showAddWorkerForm)}
                      >
                        {showAddWorkerForm ? 'Cancel' : '+ Add Worker'}
                      </button>
                    </div>

                    {/* Add Worker Form */}
                    {showAddWorkerForm && (
                      <div style={{
                        background: 'var(--dark-elevated)',
                        borderRadius: '8px',
                        padding: '1.5rem',
                        marginBottom: '1.5rem',
                        border: '1px solid var(--border-color)'
                      }}>
                        {/* Add New Worker by Name */}
                        <div style={{
                          display: 'flex',
                          gap: '1rem',
                          alignItems: 'flex-end',
                          marginBottom: '1.5rem',
                          padding: '1rem',
                          background: 'var(--dark-surface)',
                          borderRadius: '6px'
                        }}>
                          <div style={{ flex: 1 }}>
                            <label style={{ display: 'block', marginBottom: '0.5rem', color: 'var(--text-secondary)', fontSize: '0.9rem' }}>
                              Add New Worker (type name)
                            </label>
                            <input
                              type="text"
                              value={newWorkerName}
                              onChange={(e) => setNewWorkerName(e.target.value)}
                              placeholder="Enter worker name..."
                              onKeyPress={(e) => {
                                if (e.key === 'Enter' && newWorkerName.trim()) {
                                  handleAddWorkerToRoom(newWorkerName.trim());
                                  setNewWorkerName('');
                                }
                              }}
                              style={{
                                width: '100%',
                                padding: '0.75rem',
                                border: '1px solid var(--border-color)',
                                borderRadius: '4px',
                                background: 'var(--dark-elevated)',
                                color: 'var(--text-primary)'
                              }}
                            />
                          </div>
                          <button
                            onClick={() => {
                              if (newWorkerName.trim()) {
                                handleAddWorkerToRoom(newWorkerName.trim());
                                setNewWorkerName('');
                              }
                            }}
                            disabled={!newWorkerName.trim()}
                            className="add-price-btn"
                            style={{ padding: '0.75rem 1.5rem' }}
                          >
                            Add
                          </button>
                        </div>

                        {/* Or Select Existing Worker */}
                        <h4 style={{ margin: '0 0 1rem', color: 'var(--text-primary)' }}>Or Select Existing Worker</h4>
                        <div className="worker-grid" style={{
                          display: 'grid',
                          gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
                          gap: '0.75rem'
                        }}>
                          {Object.keys(allWorkers)
                            .filter(workerName => !roomWorkers.some(w => w.UserName === workerName))
                            .sort((a, b) => a.localeCompare(b))
                            .map((workerName) => (
                              <button
                                key={workerName}
                                onClick={() => handleAddWorkerToRoom(workerName)}
                                className="worker-btn"
                                style={{
                                  padding: '0.75rem 1rem',
                                  fontSize: '0.95rem',
                                  background: 'var(--dark-surface)',
                                  border: '2px solid #1874d1dc',
                                  borderRadius: '6px',
                                  cursor: 'pointer',
                                  transition: 'all 0.2s ease',
                                  color: 'var(--text-primary)'
                                }}
                              >
                                {workerName}
                              </button>
                            ))}
                          {Object.keys(allWorkers).filter(workerName => !roomWorkers.some(w => w.UserName === workerName)).length === 0 && (
                            <div style={{ gridColumn: '1 / -1', textAlign: 'center', color: 'var(--text-secondary)', padding: '1rem' }}>
                              All existing workers are already assigned to this room. Type a new name above.
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Current Workers Grid */}
                    <div className="worker-selection">
                      <div className="worker-grid" style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                        gap: '1rem'
                      }}>
                        {roomWorkers.map((worker) => (
                          <div
                            key={worker.UserName}
                            style={{
                              padding: '1rem',
                              background: 'var(--dark-surface)',
                              border: '2px solid #1874d1dc',
                              borderRadius: '8px',
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center'
                            }}
                          >
                            <div>
                              <div style={{ fontWeight: 'bold', color: 'var(--text-primary)' }}>
                                {worker.UserFullName || worker.UserName}
                              </div>
                              <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
                                {worker.UserName}
                              </div>
                            </div>
                            <button
                              onClick={() => handleRemoveWorkerFromRoom(worker.UserName)}
                              className="delete-btn"
                              style={{
                                padding: '0.5rem 0.75rem',
                                fontSize: '0.85rem'
                              }}
                            >
                              Remove
                            </button>
                          </div>
                        ))}
                        {roomWorkers.length === 0 && (
                          <div style={{ gridColumn: '1 / -1', textAlign: 'center', color: 'var(--text-secondary)', padding: '2rem' }}>
                            No workers assigned to this room yet. Click "+ Add Worker" to assign workers.
                          </div>
                        )}
                      </div>
                      <div style={{ marginTop: '1rem', color: 'var(--text-secondary)', fontSize: '0.9em' }}>
                        {roomWorkers.length} worker{roomWorkers.length !== 1 ? 's' : ''} assigned to Room {selectedWorkerRoom}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'priceList' && (
              <div className="admin-section price-list-section">
                <h2>Price List Management</h2>

                {/* Sub-tabs for different price categories */}
                <div className="price-sub-tabs">
                  <button
                    className={`price-sub-tab ${priceSubTab === 'repairs' ? 'active' : ''}`}
                    onClick={() => { setPriceSubTab('repairs'); setSearchTerm(''); setFilterType('All'); setShowAddForm(false); setEditingItem(null); }}
                  >
                    Repairs ({repairs.length})
                  </button>
                  <button
                    className={`price-sub-tab ${priceSubTab === 'accessories' ? 'active' : ''}`}
                    onClick={() => { setPriceSubTab('accessories'); setSearchTerm(''); setShowAddForm(false); setEditingItem(null); }}
                  >
                    Accessories ({accessories.length})
                  </button>
                  <button
                    className={`price-sub-tab ${priceSubTab === 'wigPricing' ? 'active' : ''}`}
                    onClick={() => { setPriceSubTab('wigPricing'); setSearchTerm(''); setShowAddForm(false); setEditingItem(null); }}
                  >
                    Wig Pricing ({wigPricing.length})
                  </button>
                </div>

                {/* Search and Filter Controls */}
                <div className="price-controls">
                  <input
                    type="text"
                    placeholder="Search..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="search-input"
                  />
                  {priceSubTab === 'repairs' && (
                    <select
                      value={filterType}
                      onChange={(e) => setFilterType(e.target.value)}
                      className="filter-bubble"
                    >
                      <option value="All">All Types</option>
                      {REPAIR_TYPES.map(type => (
                        <option key={type} value={type}>{type}</option>
                      ))}
                    </select>
                  )}
                  {priceSubTab !== 'wigPricing' && (
                    <button
                      className="add-price-btn"
                      onClick={() => { setShowAddForm(true); setEditingItem(null); }}
                    >
                      + Add New
                    </button>
                  )}
                </div>

                {priceLoading && <div className="loading">Loading...</div>}

                {/* REPAIRS TAB */}
                {priceSubTab === 'repairs' && !priceLoading && (
                  <>
                    {showAddForm && (
                      <div className="price-form">
                        <h4>Add New Repair</h4>
                        <form onSubmit={(e) => {
                          e.preventDefault();
                          const formData = new FormData(e.target);
                          handleSaveRepair({
                            RepairDescription: formData.get('description'),
                            RepairCost: parseFloat(formData.get('cost')),
                            Type: formData.get('type')
                          }, true);
                        }}>
                          <input name="description" placeholder="Repair Description" required />
                          <input name="cost" type="number" step="0.01" placeholder="Cost" required />
                          <select name="type" required>
                            {REPAIR_TYPES.map(type => (
                              <option key={type} value={type}>{type}</option>
                            ))}
                          </select>
                          <div className="form-actions">
                            <button type="submit">Save</button>
                            <button type="button" onClick={() => setShowAddForm(false)}>Cancel</button>
                          </div>
                        </form>
                      </div>
                    )}

                    <table className="price-table">
                      <thead>
                        <tr>
                          <th>Description</th>
                          <th>Type</th>
                          <th>Price</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredRepairs.map((repair) => (
                          <tr key={repair.ID}>
                            {editingItem?.ID === repair.ID ? (
                              <>
                                <td>
                                  <input
                                    value={editingItem.RepairDescription}
                                    onChange={(e) => setEditingItem({ ...editingItem, RepairDescription: e.target.value })}
                                  />
                                </td>
                                <td>
                                  <select
                                    value={editingItem.Type}
                                    onChange={(e) => setEditingItem({ ...editingItem, Type: e.target.value })}
                                  >
                                    {REPAIR_TYPES.map(type => (
                                      <option key={type} value={type}>{type}</option>
                                    ))}
                                  </select>
                                </td>
                                <td>
                                  <input
                                    type="number"
                                    step="0.01"
                                    value={editingItem.RepairCost}
                                    onChange={(e) => setEditingItem({ ...editingItem, RepairCost: parseFloat(e.target.value) })}
                                  />
                                </td>
                                <td>
                                  <button onClick={() => handleSaveRepair(editingItem)}>Save</button>
                                  <button onClick={() => setEditingItem(null)}>Cancel</button>
                                </td>
                              </>
                            ) : (
                              <>
                                <td>{repair.RepairDescription}</td>
                                <td><span className={`type-badge ${repair.Type?.toLowerCase().replace(/\s+/g, '-')}`}>{repair.Type}</span></td>
                                <td>{formatCurrency(repair.RepairCost)}</td>
                                <td>
                                  <button onClick={() => setEditingItem({ ...repair })}>Edit</button>
                                  <button className="delete-btn" onClick={() => handleDeleteRepair(repair.ID)}>Delete</button>
                                </td>
                              </>
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </>
                )}

                {/* ACCESSORIES TAB */}
                {priceSubTab === 'accessories' && !priceLoading && (
                  <>
                    {showAddForm && (
                      <div className="price-form">
                        <h4>Add New Accessory</h4>
                        <form onSubmit={(e) => {
                          e.preventDefault();
                          const formData = new FormData(e.target);
                          handleSaveAccessory({
                            Category: formData.get('category'),
                            Accessory_Description: formData.get('description'),
                            Retail_Price: parseFloat(formData.get('price'))
                          }, true);
                        }}>
                          <select name="category" required>
                            <option value="">Select Category</option>
                            {ACCESSORY_CATEGORIES.map(cat => (
                              <option key={cat} value={cat}>{cat}</option>
                            ))}
                          </select>
                          <input name="description" placeholder="Description" required />
                          <input name="price" type="number" step="0.01" placeholder="Retail Price" required />
                          <div className="form-actions">
                            <button type="submit">Save</button>
                            <button type="button" onClick={() => setShowAddForm(false)}>Cancel</button>
                          </div>
                        </form>
                      </div>
                    )}

                    <table className="price-table">
                      <thead>
                        <tr>
                          <th>Category</th>
                          <th>Description</th>
                          <th>Price</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredAccessories.map((accessory) => (
                          <tr key={accessory.ID}>
                            {editingItem?.ID === accessory.ID ? (
                              <>
                                <td>
                                  <select
                                    value={editingItem.Category}
                                    onChange={(e) => setEditingItem({ ...editingItem, Category: e.target.value })}
                                  >
                                    {ACCESSORY_CATEGORIES.map(cat => (
                                      <option key={cat} value={cat}>{cat}</option>
                                    ))}
                                  </select>
                                </td>
                                <td>
                                  <input
                                    value={editingItem.Accessory_Description}
                                    onChange={(e) => setEditingItem({ ...editingItem, Accessory_Description: e.target.value })}
                                  />
                                </td>
                                <td>
                                  <input
                                    type="number"
                                    step="0.01"
                                    value={editingItem.Retail_Price}
                                    onChange={(e) => setEditingItem({ ...editingItem, Retail_Price: parseFloat(e.target.value) })}
                                  />
                                </td>
                                <td>
                                  <button onClick={() => handleSaveAccessory(editingItem)}>Save</button>
                                  <button onClick={() => setEditingItem(null)}>Cancel</button>
                                </td>
                              </>
                            ) : (
                              <>
                                <td>{accessory.Category}</td>
                                <td>{accessory.Accessory_Description}</td>
                                <td>{formatCurrency(accessory.Retail_Price)}</td>
                                <td>
                                  <button onClick={() => setEditingItem({ ...accessory })}>Edit</button>
                                  <button className="delete-btn" onClick={() => handleDeleteAccessory(accessory.ID)}>Delete</button>
                                </td>
                              </>
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </>
                )}

                {/* WIG PRICING TAB */}
                {priceSubTab === 'wigPricing' && !priceLoading && (
                  <>
                    <p className="info-text" style={{ color: 'var(--text-secondary)', fontStyle: 'italic', marginBottom: '1rem' }}>
                      Note: You can only edit prices. To add new wig types, please contact the IT department.
                    </p>
                    <p>INSERT INTO tbl_Wig_Pricing_Source  (WigType, WigBrand, WigRetailPrice, WigCustomRetailPrice, WigInventoryType, AddedDate, Wig_Length)</p>
                    <table className="price-table">
                      <thead>
                        <tr>
                          <th>Wig Type</th>
                          <th>Length</th>
                          <th>Price</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredWigPricing.map((pricing) => (
                          <tr key={pricing.ID}>
                            {editingItem?.ID === pricing.ID ? (
                              <>
                                <td>{editingItem.WigType}</td>
                                <td>{editingItem.Wig_Length}</td>
                                <td>
                                  <input
                                    type="number"
                                    step="0.01"
                                    value={editingItem.WigRetailPrice}
                                    onChange={(e) => setEditingItem({ ...editingItem, WigRetailPrice: parseFloat(e.target.value) })}
                                  />
                                </td>
                                <td>
                                  <button onClick={() => handleSaveWigPricing(editingItem)}>Save</button>
                                  <button onClick={() => setEditingItem(null)}>Cancel</button>
                                </td>
                              </>
                            ) : (
                              <>
                                <td>{pricing.WigType}</td>
                                <td>{pricing.Wig_Length}</td>
                                <td>{formatCurrency(pricing.WigRetailPrice)}</td>
                                <td>
                                  <button onClick={() => setEditingItem({ ...pricing })}>Edit Price</button>
                                </td>
                              </>
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </>
                )}
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  );
};

export default SystemAdmin;
